import React from 'react';
import ReactTooltip from 'react-tooltip';
import {
	Loader,
	Collapsible,
	BaasicCheckbox,
	BaasicHeader,
	ImageModal,
	BaasicDropdown,
	DatePicker,
} from 'core/components';
import { observer } from 'mobx-react';
import { defaultTemplate } from 'core/hoc';
import { isSome } from 'core/utils';
import {
	FulfillmentOrderItemsList,
	FulfillmentOrderHistoryLogList,
	FulfillmentOrderErrorLogList,
	FulfillmentOrdersList,
} from 'modules/order/pages';
import {
	onAssignStatuses,
	onHoldStatuses,
	onCancelStatuses,
	onUncancelStatuses,
	onPullLabelStatuses,
	onChangeShippingStatuses,
	onProductionModeStatuses,
	onNeverShippedStatuses,
	onReoptimizeStatuses,
	onHoldReason,
	onMarkAsShippedStatuses,
	onSplitFulfillmentOrderStatuses,
	channelType,
	voidLabelStatuses,
	shipmentInfoStatusEnum,
	carrierTypes,
	orderTypes,
	shipmentDocumentSourceEnum,
	confirmShipmentStatus,
} from 'common/constants';
import { AddressValidationErrorsTooltip, CostBreakdownTooltip } from 'common/tooltips';
import ResizeObserver from 'react-resize-observer';
import { translate, formatDate } from 'core/utils';
import _ from 'lodash';
import {
	fulfillmentOrderStatus,
	onUploadBOLStatuses,
	getPriority1CarrierName,
} from 'common/constants/fulfillmentOrderConstants';
import { RouterLink } from 'mobx-state-router';
import moment from 'moment';

const OrderDetailTemplate = ({
	orderDetailViewStore,
	orderDetailViewStore: {
		canCancel,
		loading,
		rootStore,
		fulfillmentOrderItems,
		activeShipmentInfo,
		pdfURL,
		printLabel,
		printPackingSlip,
		isPrintingPackingSlipAllowed,
		fulfillmentOrderStore: { item },
		isSeller,
		isSuperAdminOrganization,
		displayPopoverButton,
		itemPrice,
		order,
		isVendorOrder,
		getWarehouseCodesForDisplay,
		isReentered,
		orderAddressData,
		initialize,
		billOfLadingSource,
		printBillOfLading,
		hasUpcLabel,
		printUpcLabel,
		hasGs1Label,
		printGs1Label,
		getProNumber,
		proNumber,
		isLtlOrder,
		isLtlShipment,
		openOrderDetailPage,
		nonCancelHoldReason,
		showFulfillmentOrderImages,
		fulfillmentOrderImageViewStore,
		addressValidationErrorsTooltipData,
		orderTypeDropdownStore,
		editOrderTypeEnabled,
		onEditOrderType,
		onCancelEditOrderType,
		onEditOrderTypeSave,
		relatedFulfillmentOrders,
		editWhShipByDateEnabled,
		editableWhShipByDateForm,
		onEditWhShipByDate,
		onCancelEditWhShipByDate,
		onEditWhShipByDateSave,
		isUpdateWhShipByDateInProgress,
		isUpdateOrderTypeInProgress,
		openAssignCarrierAccountModal,
		onConfirmShipment,
		confirmShipmentInProgress,
		confirmShipmentFailed,
		shipmentDimensions,
		palletDimensions,
	},
	t,
}) => {
	if (loading) return <Loader />;
	// Priority1 LTL orders carry the underlying freight carrier name in the stored rateMatchKey; surface it as a
	// hover tooltip on the assigned carrier service (only present for Priority1 orders).
	const priority1CarrierName = getPriority1CarrierName(item.priority1 ? item.priority1.rateMatchKey : null);
	// A Priority1 booking is the only LTL flow that captures a BOL number of its own; it is stored on the order as
	// well as on the shipment, and is the precondition for looking a PRO number up at Priority1.
	const priority1BolNumber = item.priority1 ? item.priority1.billOfLadingNumber : null;
	// A multi-item order has either more than one line item, or a single line item with quantity > 1.
	// Dimensions are only meaningful for these; single-item orders should not display them.
	const isMultiItemOrder =
		fulfillmentOrderItems.length > 1 ||
		(fulfillmentOrderItems.length === 1 && fulfillmentOrderItems[0].quantity > 1);
	return (
		<React.Fragment>
			<ReactTooltip />
			<div className="order">
				<BaasicHeader title="ORDERDETAILVIEW.ORDERDETAILS" rootStore={rootStore}>
					<ResizeObserver
						onResize={rect => {
							displayPopoverButton(rect.width);
						}}
					/>
					<ActionsRender viewStore={orderDetailViewStore} t={t} />
				</BaasicHeader>
				<div className="order__wrapper">
					<div className="order__header">
						<div className="container--fluid">
							<div className="order__header__wrapper">
								<div className="order__header__column">
									<div className="order__header__item">
										<h4 className="order__header__title type--base">{t('Seller')}</h4>
										<p data-cy={'fulfillmentOrderId'}>{item.seller.applicationTitle}</p>
									</div>
								</div>
								<div className="order__header__column">
									<div className="order__header__item">
										<h4 className="order__header__title type--base">{t('Channel')}</h4>
										<p data-cy={'fulfillmentOrderId'}>{item.order.channel.name}</p>
									</div>
								</div>
								<div className="order__header__column">
									<div className="order__header__item">
										<h4 className="order__header__title type--base">{t('ORDERDETAILVIEW.FULFILLMENTORDERID')}</h4>
										<p data-cy={'fulfillmentOrderId'}>{item.fulfillmentOrderId}</p>
									</div>
								</div>
								{item.internalNote != '' && item.internalNote != null && (
									<div className="order__header__column">
										<div className="order__header__item">
											<h4 className="order__header__title type--base">{t('ORDERDETAILVIEW.NOTE')}</h4>
											<p data-cy={'internalNote'}>{item.internalNote}</p>
										</div>
									</div>
								)}
								{(isSeller || isSuperAdminOrganization) && (
									<div className="order__header__column">
										<div className="order__header__item">
											<h4 className="order__header__title type--base">{t('ORDERDETAILVIEW.ORDERNUMBER')}</h4>
											<span>
												<RouterLink
													routerStore={rootStore.routerStore}
													routeName={'master.app.main.order.marketplace.list.detail'}
													params={{ id: item.orderId }}
												>
													{item.order.orderNumber}
												</RouterLink>
												{item.order.channel.channelType.abrv.toLowerCase() === channelType.amazon.toLowerCase() && (
													<a
														href={'https://sellercentral.amazon.com/orders-v3/order/' + item.order.orderNumber}
														target="_blank"
														rel="noopener noreferrer"
													>
														<span> | </span> <i className="icon icon--grid-action icon--amazon" />
													</a>
												)}
											</span>
										</div>
									</div>
								)}
								<div className="order__header__column">
									<div className="order__header__item">
										<h4 className="order__header__title type--base">{t('ORDERDETAILVIEW.FULFILLMENTORDERSTATUS')}</h4>

										<p data-cy={'status'}>{item.fulfillmentOrderStatus.name}</p>

										{item.fulfillmentOrderStatus.abrv === fulfillmentOrderStatus.exitScan &&
											(item.confirmShipmentStatus &&
												item.confirmShipmentStatus.name !== confirmShipmentStatus.noConfirmationNeeded) && (
												<div className="shipment-confirmation-wrapper">
													{renderConfirmShipmentIcon(item, confirmShipmentInProgress, confirmShipmentFailed)}

													{item.confirmShipmentStatus && (
														<div className="shipment-confirmation-details">
															<span className="shipment-confirmation-name">{item.confirmShipmentStatus.name}</span>

															{item.confirmShipmentTimestamp && (
																<span className="shipment-confirmation-time" data-cy={'confirmShipmentTimestamp'}>
																	{formatDate(item.confirmShipmentTimestamp, true, 'LLL')}
																</span>
															)}
														</div>
													)}
												</div>
											)}
									</div>
								</div>
								{item.cancelReason != null && (
									<div className="order__header__column">
										<div className="order__header__item">
											<h4 className="order__header__title type--base">{t('ORDERDETAILVIEW.CANCELREASON')}</h4>
											<p data-cy={'cancelReason'}>{item.cancelReason ? item.cancelReason.name : '-'}</p>
										</div>
									</div>
								)}
								{item.replacementReason != null && (
									<div className="order__header__column">
										<div className="order__header__item">
											<h4 className="order__header__title type--base">{t('ORDERDETAILVIEW.REPLACEMENTREASON')}</h4>
											<p data-cy={'replacementReason'}>{item.replacementReason ? item.replacementReason.name : '-'}</p>
										</div>
									</div>
								)}
								{activeShipmentInfo != null && !isLtlOrder && (
									<div className="order__header__column">
										<div className="order__header__item">
											<h4 className="order__header__title type--base">{t('ORDERDETAILVIEW.TRACKINGNUMBER')}</h4>
											<p data-cy={'headerTrackingNumber'}>{activeShipmentInfo.trackingNumber}</p>
										</div>
									</div>
								)}
								{/* On Hold reason */}
								<div className="order__header__column">
									<div className="order__header__item">
										<h4 className="order__header__title type--base">{t('ORDERDETAILVIEW.ONHOLDREASON')}</h4>
										<p data-cy={'onHoldReason'}>{item.onHoldReason ? item.onHoldReason.name : '-'}</p>
									</div>
								</div>
								{activeShipmentInfo != null && (
									<div className="order__header__column">
										<div className="order__header__item">
											<h4 className="order__header__title type--base">{t('ORDERDETAILVIEW.ISBUYSHIPPING')}</h4>
											<p>
												{activeShipmentInfo.isBuyShipping ? (
													<i data-cy={'isBuyShippingTrue'} className="type--color--success fas fa-check-circle" />
												) : (
													<i data-cy={'isBuyShippingFalse'} className="fas fa-times-circle"></i>
												)}
											</p>
										</div>
									</div>
								)}
								<div className="order__header__column">
									<div className="order__header__item">
										<h4 className="order__header__title type--base">{t('ORDERDETAILVIEW.ISPRIME')}</h4>
										<p>
											{item.order.isPrime ? (
												<i data-cy={'isPrimeTrue'} className="icon icon--xlrg icon--prime" />
											) : (
												<i data-cy={'isPrimeFalse'} className="icon icon--xlrg icon--prime--grey" />
											)}
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div className="order__details">
						{fulfillmentOrderImageViewStore && fulfillmentOrderImageViewStore.isOpen && (
							<ImageModal viewStore={fulfillmentOrderImageViewStore} />
						)}
						<div className="container--fluid">
							<div className="row">
								<div className="col col-sml-12">
									<div className="order__box">
										<h4 className="order__box__title">{t('GENERALINFORMATION')}</h4>
										<div className="order__box__content">
											<div
												className={
													item.productionMode
														? 'flex__wrapper flex__wrapper--justify--between is-warning'
														: 'flex__wrapper flex__wrapper--justify--between'
												}
											>
												<div className="flex__column">
													<div>
														<div className="order__header__item">
															<h4 className="order__header__title">{t('ORDERDETAILVIEW.CUSTOMER')}</h4>
														</div>
														<div className="order__box__content">
															{orderAddressData.buyerName != null && (
																<span>
																	<p className="order__label">{t('ORDERDETAILVIEW.CUSTOMERNAME')}</p>
																	<p data-cy={'customerBuyerName1'} className="fs-mask">
																		{orderAddressData.buyerName}
																	</p>
																</span>
															)}

															<p className="order__label spc--top--sml">{t('ORDERDETAILVIEW.EMAIL')}</p>
															<p data-cy={'customerCustomerEmail'}>{orderAddressData.buyerEmail}</p>

															<p className="order__label spc--top--sml">
																<span>{t('ORDERDETAILVIEW.ADDRESS')}</span>
																{orderAddressData.addressChanged && (
																	<i
																		data-tip={t('ORDERVIEW.ADDRESSCHANGED')}
																		className="icon icon--xmed icon--location-check--primary spc--left--nano"
																	></i>
																)}

																<span>
																	{isSome(addressValidationErrorsTooltipData) &&
																		addressValidationErrorsTooltipData.length > 0 && (
																			<AddressValidationErrorsTooltip
																				addressValidationErrors={addressValidationErrorsTooltipData}
																			/>
																		)}
																</span>
															</p>
															<p data-cy={'customerBuyerName2'} className="fs-mask">
																{orderAddressData.buyerName}
															</p>
															<p data-cy={'customerShippingAddress1'} className="fs-mask">
																{orderAddressData.shippingAddress1}
															</p>
															{orderAddressData.shippingAddress2 != '' && (
																<p data-cy={'customerShippingAddress2'} className="fs-mask">
																	{orderAddressData.shippingAddress2}
																</p>
															)}
															{orderAddressData.shippingAddress3 != '' && (
																<p data-cy={'customerShippingAddress3'} className="fs-mask">
																	{orderAddressData.shippingAddress3}
																</p>
															)}
															<div>
																{/* separate spans are used for easier identification in frontend tests */}
																<span data-cy={'customerCity'} id="customer-city">
																	{orderAddressData.shippingCity}
																</span>
																<span>, </span>
																<span data-cy={'customerState'} id="customer-state">
																	{orderAddressData.shippingCoreCountryState
																		? orderAddressData.shippingCoreCountryState.name
																		: null}
																</span>
																<span> </span>
																<span data-cy={'customerZipCode'} id="customer-zipCode">
																	{orderAddressData.shippingZipCode}
																</span>
															</div>
															{orderAddressData.buyerPhone != null && (
																<span>
																	<p className="order__label spc--top--sml">{t('ORDERDETAILVIEW.PHONE')}</p>
																	<p data-cy={'customerBuyerPhone'} className="fs-mask">
																		{orderAddressData.buyerPhone}
																	</p>
																</span>
															)}
														</div>
													</div>
												</div>
												<div className="flex__column">
													<div>
														<div className="order__header__item">
															<h4 className="order__header__title">{t('ORDERDETAILVIEW.SHIPPING')}</h4>
														</div>
														<div className="order__box__content">
															<p className="order__label">
																<span>{t('ORDERDETAILVIEW.ADDRESS')}</span>
																{orderAddressData.addressChanged && (
																	<i
																		data-tip={t('ORDERVIEW.ADDRESSCHANGED')}
																		className="icon icon--xmed icon--location-check--primary spc--left--nano"
																	></i>
																)}

																<span>
																	{isSome(addressValidationErrorsTooltipData) &&
																		addressValidationErrorsTooltipData.length > 0 && (
																			<AddressValidationErrorsTooltip
																				addressValidationErrors={addressValidationErrorsTooltipData}
																			/>
																		)}
																</span>
															</p>
															<p data-cy={'shippingBuyerName'} className="fs-mask">
																{orderAddressData.buyerName}
															</p>
															<p data-cy={'shippingShippingAddress1'} className="fs-mask">
																{orderAddressData.shippingAddress1}
															</p>
															{orderAddressData.shippingAddress2 != '' && (
																<p data-cy={'shippingShippingAddress2'} className="fs-mask">
																	{orderAddressData.shippingAddress2}
																</p>
															)}
															{orderAddressData.shippingAddress3 != '' && (
																<p data-cy={'shippingShippingAddress3'} className="fs-mask">
																	{orderAddressData.shippingAddress3}
																</p>
															)}
															<div>
																{/* separate spans are used for easier identification in frontend tests */}
																<span data-cy={'shippingCity'} id="shipping-city">
																	{orderAddressData.shippingCity}
																</span>
																<span>, </span>
																<span data-cy={'shippingState'} id="shipping-state">
																	{orderAddressData.shippingCoreCountryState
																		? orderAddressData.shippingCoreCountryState.name
																		: null}
																</span>
																<span> </span>
																<span data-cy={'shippingZipCode'} id="shipping-zipCode">
																	{orderAddressData.shippingZipCode}
																</span>
															</div>
															{orderAddressData.buyerPhone != null && (
																<span>
																	<p className="order__label spc--top--sml">{t('ORDERDETAILVIEW.PHONE')}</p>
																	<p data-cy={'shippingBuyerPhone'} className="fs-mask">
																		{orderAddressData.buyerPhone}
																	</p>
																</span>
															)}
															<p className="order__label spc--top--sml">
																{t('ORDERDETAILVIEW.ASSIGNEDCARRIERSERVICE')}
															</p>
															<p data-cy={'assignedCarrierService'}>
																{item.carrierService ? item.carrierService.name : '-'}
															</p>

															<p className="order__label spc--top--sml">
																<span className="spc--right--sml">{t('ORDERDETAILVIEW.ASSIGNEDCARRIER')}</span>
																{isSuperAdminOrganization && isLtlOrder && (
																	<i
																		title={t('EDIT')}
																		className="fa fa-edit k-custom-clickable"
																		onClick={openAssignCarrierAccountModal}
																	></i>
																)}
															</p>
															<span data-cy={'assignedCarrier'}>{item.carrier ? item.carrier.name : '-'}</span>
															{priority1CarrierName && (
																<i
																	className="spc--left--tny fas fa-info-circle"
																	data-tip={`Priority1 Carrier: ${priority1CarrierName}`}
																	data-cy={'priority1CarrierTooltip'}
																></i>
															)}

															<p className="order__label spc--top--sml">{t('ORDERDETAILVIEW.SHIPBYDATE')}</p>
															{isReentered && (
																<React.Fragment>
																	{formatDate(item.channelNewShipByDate, false, 'L')}
																	<i
																		data-tip={t('TOOLTIP.NEWSHIPBYDATE', {
																			date: formatDate(item.channelShipByDate, false, 'L'),
																		})}
																		data-cy={'newShipByDateTooltip'}
																		className="spc--left--tny fas fa-exclamation-circle"
																	/>
																</React.Fragment>
															)}
															{!isReentered && (
																<p data-cy={'shipByDate'}>{formatDate(item.channelShipByDate, false, 'L')}</p>
															)}

															{activeShipmentInfo && (
																<span>
																	<p className="order__label spc--top--sml">{t('ORDERDETAILVIEW.SHIPPINGCOST')}</p>

																	{activeShipmentInfo.shippingCostSurcharge !== null &&
																	activeShipmentInfo.shippingCostSurcharge !== 0 ? (
																		<React.Fragment>
																			<p data-cy="shippingCost" className="order__highlight">
																				{`$${activeShipmentInfo.shippingCostTotal}`}
																			</p>
																			<CostBreakdownTooltip
																				cypressId="shippingShippingCostTooltip"
																				idValue={activeShipmentInfo.id}
																				shippingCost={activeShipmentInfo.cost}
																				surplusCharge={activeShipmentInfo.shippingCostSurcharge}
																				total={activeShipmentInfo.shippingCostTotal}
																				displayIcon={true}
																			/>
																		</React.Fragment>
																	) : (
																		<p data-cy="shippingCost" className="order__highlight">
																			{`$${activeShipmentInfo.shippingCostTotal}`}
																		</p>
																	)}
																</span>
															)}

															<span>
																<p className="order__label spc--top--sml">{t('ORDERDETAILVIEW.ASSIGNEDRATE')}</p>

																{isSome(item.assignedRateTotal) ? (
																	<React.Fragment>
																		<p data-cy="assignedRate" className="order__highlight">
																			{`$${item.assignedRateTotal}`}
																		</p>

																		{item.assignedRateSurcharge !== null && item.assignedRateSurcharge !== 0 && (
																			<CostBreakdownTooltip
																				cypressId="shippingAssignedRateTooltip"
																				idValue={item.id}
																				shippingCost={item.assignedRate}
																				surplusCharge={item.assignedRateSurcharge}
																				total={item.assignedRateTotal}
																				displayIcon={true}
																			/>
																		)}
																	</React.Fragment>
																) : (
																	<p>{'-'}</p>
																)}
															</span>

															{activeShipmentInfo && activeShipmentInfo.zone && (
																<span>
																	<p data-cy={'shippingZone'} className="order__label spc--top--sml">
																		{t('ORDERDETAILVIEW.ZONE')}
																	</p>
																	{activeShipmentInfo.zone}
																</span>
															)}
															{item.includedBenefits && (
																<span>
																	<p data-cy={'includedBenefits'} className="order__label spc--top--sml">
																		{'Included Benefits'}
																	</p>
																	{item.includedBenefits}
																</span>
															)}
															{item.excludedBenefits && (
																<span>
																	<p data-cy={'excludedBenefits'} className="order__label spc--top--sml">
																		{'Excluded Benefits'}
																	</p>
																	{item.excludedBenefits}
																</span>
															)}
															{palletDimensions && palletDimensions.length > 0 && activeShipmentInfo ? (
																<span>
																	<p data-cy={'palletDimensions'} className="order__label spc--top--sml">
																		{t('ORDERDETAILVIEW.PALLETDIMENSIONS')}
																	</p>
																	{palletDimensions.map((pd, index) => (
																		<p key={index} data-cy={`palletDimensions-${index}`}>
																			<span>{pd.display}</span>
																			<i
																				data-for={`palletDimensionsTooltip-${index}`}
																				data-tip=""
																				className="fa fa-info-circle spc--left--tny"
																				data-cy={`palletDimensionsInfo-${index}`}
																			></i>
																			<ReactTooltip id={`palletDimensionsTooltip-${index}`}>{pd.tooltip}</ReactTooltip>
																		</p>
																	))}
																</span>
															) : (
																!!(isMultiItemOrder && activeShipmentInfo && shipmentDimensions) && (
																	<span>
																		<p data-cy={'shippingZone'} className="order__label spc--top--sml">
																			{t('ORDERDETAILVIEW.DIMENSIONS')}
																		</p>
																		<p data-cy={'dimensions'}>
																			<span>{shipmentDimensions.display}</span>
																			<i
																				data-for={`dimensionsTooltip-${activeShipmentInfo.id}`}
																				data-tip=""
																				className="fa fa-info-circle spc--left--tny"
																				data-cy={'dimensionsInfo'}
																			></i>
																			<ReactTooltip id={`dimensionsTooltip-${activeShipmentInfo.id}`}>
																				{shipmentDimensions.tooltip}
																			</ReactTooltip>
																		</p>
																	</span>
																)
															)}
														</div>
													</div>
												</div>
												<div className="flex__column flex__column--warehouse">
													<div>
														<div className="order__header__item">
															<h4 className="order__header__title">{t('ORDERDETAILVIEW.WAREHOUSE')}</h4>
														</div>
														<div className="order__box__content">
															<p className="order__label">{t('ORDERDETAILVIEW.WAREHOUSENAME')}</p>
															<p data-cy={'warehouseWarehouseName'}>
																{item.warehouseOrganization ? item.warehouseOrganization.applicationTitle : '-'}
															</p>

															<p className="order__label spc--top--sml">{t('ORDERDETAILVIEW.CODE')}</p>
															<p data-cy={'warehouseCode'}>
																{item.warehouseOrganization && item.warehouseOrganization.convertWarehouses
																	? getWarehouseCodesForDisplay(item)
																	: '-'}
															</p>

															<p className="order__label spc--top--sml">{t('Pallet')}</p>
															<p data-cy={'pallet'}>
																{isSome(activeShipmentInfo) && isSome(activeShipmentInfo.pallet)
																	? activeShipmentInfo.pallet.barcode
																	: '-'}
															</p>

															<p className="order__label spc--top--sml">
																<span className="spc--right--sml">{t('Warehouse Ship By Date')}</span>
																{isSuperAdminOrganization &&
																	(editWhShipByDateEnabled ? (
																		<span>
																			{isUpdateWhShipByDateInProgress ? (
																				<div className="spinner-inline-small"></div>
																			) : (
																				<span>
																					<span>
																						<span className="spc--right--sml">
																							<i
																								title={t('SAVE')}
																								className="fa fa-check k-custom-clickable "
																								onClick={async () => await onEditWhShipByDateSave()}
																							></i>
																						</span>
																					</span>
																					<span className="spc--right--sml">
																						<i
																							title={t('CANCEL')}
																							className="fa fa-ban k-custom-clickable"
																							onClick={() => onCancelEditWhShipByDate()}
																						></i>
																					</span>
																				</span>
																			)}
																		</span>
																	) : (
																		<i
																			title={t('EDIT')}
																			className="fa fa-edit k-custom-clickable"
																			onClick={() => onEditWhShipByDate()}
																		></i>
																	))}
															</p>

															{isSuperAdminOrganization && editWhShipByDateEnabled ? (
																<div>
																	<DatePicker
																		field={editableWhShipByDateForm.$('date')}
																		format={moment.localeData().longDateFormat('L')}
																		disabled={!editWhShipByDateEnabled}
																		maxDate={new Date(item.order.shipByDate)}
																		rootStore={rootStore}
																		exceedsMaxDateErrorMessage={`Warehouse Ship By Date must be before ${formatDate(
																			item.order.shipByDate,
																			false,
																			'L'
																		)}`}
																	/>
																</div>
															) : (
																<p data-cy={'whShipByDate'}>
																	{isSome(item.whShipByDate) ? formatDate(item.whShipByDate, false, 'L') : '-'}
																</p>
															)}
														</div>

														<div>
															<div className="order__header__item">
																<h4 className="order__header__title">{t('ORDERDETAILVIEW.REQUIREDSHIPPING')}</h4>
															</div>
															<div className="order__box__content">
																<p className="order__label">{t('ORDERDETAILVIEW.REQUIREDWAREHOUSE')}</p>
																<p data-cy={'requiredWarehouse'}> {item.order.requiredWarehouse}</p>

																<p className="order__label spc--top--sml">
																	{t('ORDERDETAILVIEW.REQUIREDCARRIERSERVICE')}
																</p>
																<p data-cy={'requiredCarrierService'}> {item.order.requiredCarrierService}</p>

																<p className="order__label spc--top--sml">{t('ORDERDETAILVIEW.REQUIREDCARRIER')}</p>
																<p data-cy={'requiredCarrier'}> {_.get(item, 'order.requiredCarrier.name')}</p>
															</div>
															<div className="order__box__content">
																{item.order.channelCarrierCode && (
																	<React.Fragment>
																		<p className="order__label">{t('ORDERDETAILVIEW.CHANNELCARRIERCODE')}</p>
																		<p data-cy={'channelCarrierCode'}> {item.order.channelCarrierCode}</p>
																	</React.Fragment>
																)}
																{item.order.channelServiceCode && (
																	<React.Fragment>
																		<p className="order__label spc--top--sml">
																			{t('ORDERDETAILVIEW.CHANNELSERVICECODE')}
																		</p>
																		<p data-cy={'channelServiceCode'}> {item.order.channelServiceCode}</p>
																	</React.Fragment>
																)}
															</div>
														</div>
													</div>
												</div>
												<div className="flex__column flex__column--order">
													<div>
														<div className="order__header__item">
															<h4 className="order__header__title">{t('ORDERDETAILVIEW.MASTERORDER')}</h4>
														</div>
														<div className="order__box__content">
															<p className="order__label">{t('ORDERDETAILVIEW.ORDERDATE')}</p>
															<p data-cy={'orderDate'}>{formatDate(item.order.channelOrderDate, false, 'L')}</p>

															<p className="order__label spc--top--sml">{t('Deliver By Date')}</p>
															<p data-cy={'deliverByDate'}>
																{isSome(item.order.channelDeliverByDate)
																	? formatDate(item.order.channelDeliverByDate, false, 'L')
																	: '-'}
															</p>

															<p className="order__label spc--top--sml">{t('Confirmation Type')}</p>
															<p data-cy={'confirmationType'}>
																{isSome(item.order.confirmationType) ? item.order.confirmationType.name : '-'}
															</p>

															<p className="order__label spc--top--sml">
																<span className="spc--right--sml">{t('Order Type')}</span>

																{isSuperAdminOrganization &&
																	(editOrderTypeEnabled ? (
																		<span>
																			{isUpdateOrderTypeInProgress ? (
																				<span>
																					<div className="spinner-inline-small"></div>
																				</span>
																			) : (
																				<span>
																					<span className="spc--right--sml">
																						<i
																							title={t('SAVE')}
																							className="fa fa-check k-custom-clickable "
																							onClick={async () => await onEditOrderTypeSave()}
																						></i>
																					</span>
																					<span className="spc--right--sml">
																						<i
																							title={t('CANCEL')}
																							className="fa fa-ban k-custom-clickable"
																							onClick={() => onCancelEditOrderType()}
																						></i>
																					</span>
																				</span>
																			)}
																		</span>
																	) : (
																		<i
																			title={t('EDIT')}
																			className="fa fa-edit k-custom-clickable"
																			onClick={() => onEditOrderType()}
																		></i>
																	))}
															</p>

															{isSuperAdminOrganization && editOrderTypeEnabled ? (
																<div>
																	<BaasicDropdown
																		isDisabled={!editOrderTypeEnabled}
																		dropdownStore={orderTypeDropdownStore}
																		isClearable={true}
																	/>
																</div>
															) : (
																<p data-cy={'orderType'}>
																	{' '}
																	{isSome(item.order.orderType) ? item.order.orderType.name : '-'}{' '}
																</p>
															)}

															{item.order.customerServiceOrderNumber != '' && (
																<React.Fragment>
																	<div className="separator--primary spc--top--sml spc--bottom--sml"></div>
																	<p className="order__label">{t('ORDERDETAILVIEW.CUSTOMERSERVICEORDERNUMBER')}</p>
																	<p data-cy={'customerServiceOrderNumber'}>{item.order.customerServiceOrderNumber}</p>
																</React.Fragment>
															)}

															{(item.order.ref1 || item.order.ref2 || item.order.ref3) && (
																<React.Fragment>
																	<div className="separator--primary spc--top--sml spc--bottom--sml"></div>
																	{item.order.ref1 && (
																		<React.Fragment>
																			<p className="order__label">{t('ORDERDETAILVIEW.REF1')}</p>
																			<p>{item.order.ref1}</p>
																		</React.Fragment>
																	)}
																	{item.order.ref2 && (
																		<React.Fragment>
																			<p className="order__label">{t('ORDERDETAILVIEW.REF2')}</p>
																			<p>{item.order.ref2}</p>
																		</React.Fragment>
																	)}
																	{item.order.ref3 && (
																		<React.Fragment>
																			<p className="order__label">{t('ORDERDETAILVIEW.REF3')}</p>
																			<p>{item.order.ref3}</p>
																		</React.Fragment>
																	)}
																</React.Fragment>
															)}

															{item.order.source != '' && (
																<React.Fragment>
																	<div className="separator--primary spc--top--sml spc--bottom--sml"></div>
																	<p data-cy={'channelSource'} className="order__label">
																		{t('ORDERDETAILVIEW.CHANNELSOURCE')}
																	</p>
																	<p>{item.order.source}</p>
																</React.Fragment>
															)}

															<div className="separator--primary spc--top--sml spc--bottom--sml"></div>

															<div className="row">
																<div className="col col-sml-6">
																	<p className="order__label">{t('ORDERDETAILVIEW.ITEMPRICE')}</p>
																</div>
																<div className="col col-sml-6">
																	<p data-cy={'itemPrice'} className="type--right">
																		{itemPrice}
																	</p>
																</div>
															</div>

															<div className="row spc--top--sml">
																<div className="col col-sml-6">
																	<p className="order__label">{t('ORDERDETAILVIEW.TOTALTAX')}</p>
																</div>
																<div className="col col-sml-6">
																	<p data-cy={'totalTax'} className="type--right">
																		{order.taxPaid}
																	</p>
																</div>
															</div>
															<div className="row spc--top--sml">
																<div className="col col-sml-6">
																	<p className="order__label">{t('ORDERDETAILVIEW.TOTALSHIPPING')}</p>
																</div>
																<div className="col col-sml-6">
																	<p data-cy={'totalShipping'} className="type--right">
																		{order.shippingPaid}
																	</p>
																</div>
															</div>
															<div className="row spc--top--sml">
																<div className="col col-sml-6">
																	<p className="order__label">{t('ORDERDETAILVIEW.SUBTOTAL')}</p>
																</div>
																<div className="col col-sml-6">
																	<p data-cy={'subtotal'} className="type--right">
																		{itemPrice}
																	</p>
																</div>
															</div>
															<div className="row spc--top--sml">
																<div className="col col-sml-6">
																	<p className="order__label">{t('ORDERDETAILVIEW.DISCOUNT')}</p>
																</div>
																<div className="col col-sml-6">
																	<p data-cy={'discount'} className="type--right">
																		{order.discount * -1}
																	</p>
																</div>
															</div>
															<div className="row spc--top--sml">
																<div className="col col-sml-6">
																	<p className="order__label type--lrg">{t('ORDERDETAILVIEW.TOTALPAID')}</p>
																</div>
																<div className="col col-sml-6">
																	<p data-cy={'totalPaid'} className="type--right type--lrg">
																		{order.amountPaid}
																	</p>
																</div>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
							{
								<div className="row spc--top--sml">
									<div className="col col-sml-12">
										<div className="order__box">
											<h4 className="order__box__title">{t('ORDERDETAILVIEW.LABELS')}</h4>
											<div className="order__box__content">
												<div className="flex__wrapper flex__wrapper--justify--between">
													<div className="flex__column">
														{!isLtlShipment &&
															_.every(item.shipmentInfos, shipmentInfo => {
																return !!shipmentInfo.trackingNumber && shipmentInfo.trackingNumber.length !== 0;
															}) &&
															item.shipmentInfos.length > 0 && (
																<span className="order__label">{t('ORDERDETAILVIEW.TRACKINGNUMBERS')}:</span>
															)}
														{_.map(item.shipmentInfos, (label, idx) => {
															// LTL PRO/BOL numbers are rendered together by <LtlLabelsTable /> below the loop.
															if (isLtlShipment) return <React.Fragment key={idx}></React.Fragment>;
															if (label.status == shipmentInfoStatusEnum.voided) {
																return (
																	<p
																		data-cy={'voidedTrackingNumbers'}
																		key={idx}
																		style={
																			label.status == shipmentInfoStatusEnum.voided
																				? { textDecoration: 'line-through', color: 'grey' }
																				: {}
																		}
																	>
																		{label.trackingNumber}
																	</p>
																);
															} else {
																return isSome(item.carrierService) ? (
																	<p data-cy={'activeTrackingNumber'} key={idx}>
																		{!isLtlOrder && !isLtlShipment ? (
																			<a
																				href={
																					item.carrierService.carrierType.name === carrierTypes.passport
																						? `https://track${
																								process.env.NODE_ENV === 'production' ? '' : '-stg'
																						  }.passportshipping.com/${label.trackingNumber}`
																						: item.carrierService.carrierType.name === carrierTypes.usps
																						? 'https://www.stamps.com/tracking-details/?t=' + label.trackingNumber
																						: item.carrierService.carrierType.name === carrierTypes.fedex
																						? 'https://www.fedex.com/apps/fedextrack/?action=track&language=english&tracknumbers=' +
																						  label.trackingNumber
																						: 'https://www.ups.com/track?loc=en_US&requester=QUIC&tracknum=' +
																						  label.trackingNumber.split('-')[0]
																				}
																				target="_blank"
																			>
																				{label.trackingNumber}
																			</a>
																		) : (
																			<React.Fragment>{label.trackingNumber}</React.Fragment>
																		)}
																	</p>
																) : (
																	<p key={idx}>{label.trackingNumber}</p>
																);
															}
														})}
														{isLtlShipment && item.shipmentInfos.length > 0 && (
															<LtlLabelsTable
																shipmentInfos={item.shipmentInfos}
																proNumber={proNumber}
																priority1BolNumber={priority1BolNumber}
																t={t}
															/>
														)}
														{/* Print Label */}
														{pdfURL && !canCancel && !isLtlOrder && (
															<React.Fragment>
																<button
																	data-cy={'printLabel'}
																	className="btn btn--med btn--primary spc--top--med spc--right--sml"
																	type="button"
																	onClick={printLabel}
																>
																	{t('PRINTLABEL')}
																</button>
															</React.Fragment>
														)}
														{isPrintingPackingSlipAllowed && (
															<button
																data-cy={'printPackingSlip'}
																className="btn btn--med btn--primary spc--top--med spc--right--sml"
																type="button"
																onClick={printPackingSlip}
															>
																{t('PRINTPACKINGSLIP')}
															</button>
														)}
														{(isLtlOrder || (isLtlShipment && activeShipmentInfo)) && (
															<button
																data-cy={'printBol'}
																className="btn btn--med btn--primary spc--top--med spc--right--sml"
																type="button"
																onClick={printBillOfLading}
																data-tip={billOfLadingSource}
															>
																{t('PRINTBOL')}
															</button>
														)}
														{hasUpcLabel && (
															<button
																data-cy={'printUpcLabel'}
																className="btn btn--med btn--primary spc--top--med spc--right--sml"
																type="button"
																onClick={printUpcLabel}
															>
																{t('PRINTUPCLABEL')}
															</button>
														)}
														{hasGs1Label && (
															<button
																data-cy={'printGs1Label'}
																className="btn btn--med btn--primary spc--top--med spc--right--sml"
																type="button"
																onClick={printGs1Label}
															>
																{t('PRINTGS1LABEL')}
															</button>
														)}
														{/* Without a Priority1 BOL number there is nothing to look a PRO up by. */}
														{isLtlShipment &&
															priority1BolNumber &&
															activeShipmentInfo &&
															!activeShipmentInfo.trackingNumber &&
															!proNumber && (
																<button
																	data-cy={'getProNumber'}
																	className="btn btn--med btn--primary spc--top--med spc--right--sml"
																	type="button"
																	onClick={getProNumber}
																>
																	{t('GETPRONUMBER')}
																</button>
															)}
														{item.fulfillmentOrderImages && item.fulfillmentOrderImages.length > 0 && (
															<button
																data-cy={'showFulfillmentOrderImages'}
																className="btn btn--med btn--primary spc--top--med spc--right--sml"
																type="button"
																onClick={showFulfillmentOrderImages}
															>
																{t('SHOWIMAGE')}
															</button>
														)}
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							}
						</div>
						<div data-cy={'items'} className="order__box--no-padd spc--top--med">
							<Collapsible expanded={true} title={t('ORDERDETAILVIEW.FULFILLMENTORDERITEMS')}>
								<FulfillmentOrderItemsList data={fulfillmentOrderItems.slice()} />
							</Collapsible>
						</div>
						<div data-cy={'relatedFulfillmentOrders'} className="order__box--no-padd spc--top--med">
							<Collapsible expanded={true} title={t('Related Fulfillment Orders')}>
								<FulfillmentOrdersList data={relatedFulfillmentOrders.slice()} />
							</Collapsible>
						</div>
						<div data-cy={'viewActivity'} className="spc--top--med">
							<Collapsible title={translate(['FULFILLMENTORDERVIEW.HISTORYLOG.VIEWACTIVITY'])}>
								<FulfillmentOrderHistoryLogList orderId={item.fulfillmentOrderId} />
							</Collapsible>
						</div>
					</div>
				</div>
			</div>
			<iframe id="pdf-print" name="pdf-print" src={pdfURL} style={{ display: 'none' }}></iframe>
		</React.Fragment>
	);
};

const ActionsRender = observer(({ viewStore, t }) => {
	const { shouldDisplayActionButton } = viewStore;
	return (
		<div className="layout__title--flex--end">
			{shouldDisplayActionButton == false ? (
				<Actions viewStore={viewStore} t={t} />
			) : (
				<div className="popover" onClick={() => (viewStore.actionMenuOpen = !viewStore.actionMenuOpen)}>
					<button className={'popover__button popover__button--header' + (viewStore.actionMenuOpen ? ' active' : '')}>
						<i className="fa fa-cogs spc--right--tny" />
						<span className="spc--right--tny">{t('ACTIONS')}</span>
						<i className="fa fa-angle-down spc--left--sml" />
					</button>
					<div className={'popover__menu w--220--px' + (viewStore.actionMenuOpen ? ' active' : '')}>
						<ul>
							<Actions viewStore={viewStore} t={t} />
						</ul>
					</div>
				</div>
			)}
		</div>
	);
});

const Actions = observer(({ viewStore, t }) => {
	const {
		openModal,
		openAssignmentModal,
		openOnHoldModal,
		openCancelModal,
		openChangeShippingModal,
		openMarkAsShippedModal,
		openEditOrderModal,
		isWarehouseAdministrator,
		isSmartshipWarehouseAdministrator,
		hasLabel,
		isSeller,
		fulfillmentOrder,
		rootStore,
		fulfillmentOrderStore: { item },
		isSuperAdminOrganization,
		getProductionModeConfirmModalProps,
		getVoidBOLConfirmModalProps,
		isBoxRequired,
		isVendorOrder,
		isLtlOrder,
		isLtlShipment,
		openSplitFulfillmentOrderModal,
		editFulfillmentOrderSplit,
		getCancelConfirmModalProps,
		openConfirmationModal,
		openUploadDocumentModal,
		canUploadAnyDocument,
		getPullLabelConfirmModalProps,
		getReoptimizeConfirmModalProps,
		openVoidLabelConfirmModal,
		openNeverShippedModal,
		timezone,
		format,
		isExternalOrder,
		getPullBOLConfirmModalProps,
		getUncancelConfirmModalProps,
		nonCancelHoldReason,
		errorLogClass,
		getRetryConfirmShipmentModalProps,
	} = viewStore;
	return (
		<React.Fragment>
			{/* Retry Confirm Shipment */}
			{item.fulfillmentOrderStatus.abrv === fulfillmentOrderStatus.exitScan &&
				item.confirmShipmentStatus &&
				item.confirmShipmentStatus.name !== confirmShipmentStatus.noConfirmationNeeded &&
				item.confirmShipmentStatus.name !== confirmShipmentStatus.confirmed &&
				isSuperAdminOrganization && (
					<div className="popover__menu__item">
						<button
							className="btn btn--white"
							type="button"
							onClick={() => openConfirmationModal(getRetryConfirmShipmentModalProps())}
						>
							<i
								title={t('Retry Confirm Shipment')}
								className="icon icon--grid-action icon--confirm-shipment spc--right--sml k-custom-clickable"
							></i>
							{t('Retry Confirm Shipment')}
						</button>
					</div>
				)}
			{/* Error Log */}
			<div className="popover__menu__item">
				<button
					className="btn btn--white"
					type="button"
					onClick={() =>
						openModal({
							data: {
								orderId: item.fulfillmentOrderId,
								errorsList: item.fulfillmentOrderErrors,
								hasErrors: item.hasErrors,
							},
							component: FulfillmentOrderErrorLogList,
						})
					}
				>
					<i data-cy={'viewItemErrorLog'} title={t('ERRORLOG')} className={errorLogClass}></i>
					{t('ERRORLOG')}
				</button>
			</div>
			{/* Pull Label */}
			{(isWarehouseAdministrator || isSeller || isSuperAdminOrganization || isSmartshipWarehouseAdministrator) &&
				!isBoxRequired &&
				!hasLabel &&
				!isExternalOrder &&
				!isLtlOrder &&
				!isLtlShipment &&
				rootStore.authStore.hasPiiAccess &&
				_.findIndex(onPullLabelStatuses, status => status == item.fulfillmentOrderStatus.abrv) != -1 && (
					<div className="popover__menu__item">
						<button
							data-cy={'pullLabel'}
							className="btn btn--white"
							onClick={() => openConfirmationModal(getPullLabelConfirmModalProps())}
						>
							<i title={t('PULLLABEL')} className="far fa-arrow-alt-circle-down spc--right--sml k-custom-clickable"></i>
							{t('PULLLABEL')}
						</button>
					</div>
				)}
			{/* Upload Document */}
			{canUploadAnyDocument && (
				<div className="popover__menu__item">
					<button data-cy={'uploadDocument'} className="btn btn--white" onClick={openUploadDocumentModal}>
						<i title={t('UPLOADDOCUMENT')} className="fa fa-chevron-circle-up spc--right--tny k-custom-clickable"></i>
						{t('UPLOADDOCUMENT')}
					</button>
				</div>
			)}
			{/* Pull BOL */}
			{isLtlOrder &&
				_.get(fulfillmentOrder, 'billOfLading.source') == null &&
				(fulfillmentOrder.order.channel.channelType.name === channelType.sellerCloud ||
					fulfillmentOrder.order.channel.shipmentDocumentPullSource.toLowerCase() === 'banyan') &&
				rootStore.authStore.hasPiiAccess &&
				_.findIndex(onUploadBOLStatuses, status => status == item.fulfillmentOrderStatus.abrv) != -1 && (
					<div className="popover__menu__item">
						<button
							data-cy={'pullBOL'}
							className="btn btn--white"
							onClick={() => openConfirmationModal(getPullBOLConfirmModalProps())}
						>
							<i title={t('pullBOL')} className="fa fa-chevron-circle-down spc--right--tny k-custom-clickable"></i>
							{t('ORDERVIEW.PULLBOL')}
						</button>
					</div>
				)}
			{/* Assign Carrier and Warehouse */}
			{_.findIndex(onAssignStatuses, status => status == item.fulfillmentOrderStatus.abrv) != -1 && (
				<div className="popover__menu__item">
					<button
						data-cy={'assignCarrierAndWarehouse'}
						className="btn btn--white"
						type="button"
						onClick={openAssignmentModal}
					>
						<i title={t('ASSIGN')} className="fa fa-tag spc--right--tny k-custom-clickable"></i>
						{t('FULFILLMENTORDER.ASSIGNMODAL.TITLE')}
					</button>
				</div>
			)}
			{/* On Hold */}
			{(isSeller || isSuperAdminOrganization) &&
				_.findIndex(onHoldStatuses, status => status == item.fulfillmentOrderStatus.abrv) != -1 && (
					<div className="popover__menu__item">
						<button data-cy={'onHold'} className="btn btn--white" onClick={openOnHoldModal}>
							<i title={t('ONHOLD')} className="fa fa-hand-paper spc--right--tny k-custom-clickable"></i>
							{t('ONHOLDVIEW.ONHOLD')}
						</button>
					</div>
				)}
			{/* Change shipping */}
			{(isSeller || isSuperAdminOrganization) &&
				!isVendorOrder &&
				(item.onHoldReason == null || item.onHoldReason.abrv != onHoldReason.toBeCancelled) &&
				_.findIndex(onChangeShippingStatuses, status => status == item.fulfillmentOrderStatus.abrv) != -1 && (
					<div className="popover__menu__item">
						<button data-cy={'changeShipping'} className="btn btn--white" onClick={openChangeShippingModal}>
							<i title={t('CHANGESHIPPING')} className="fa fa-exchange-alt spc--right--sml k-custom-clickable"></i>
							{t('CHANGESHIPPINGVIEW.TITLE')}
						</button>
					</div>
				)}
			{(isSeller || isSuperAdminOrganization) &&
				((_.findIndex(onSplitFulfillmentOrderStatuses, status => status == item.fulfillmentOrderStatus.abrv) != -1 &&
					(item.fulfillmentOrderItems.length > 1 ||
						(item.fulfillmentOrderItems !== null && item.fulfillmentOrderItems[0].quantity > 1))) ||
					editFulfillmentOrderSplit) && (
					<div className="popover__menu__item">
						<button data-cy={'splitBySuperAdmin'} className="btn btn--white" onClick={openSplitFulfillmentOrderModal}>
							<i
								title={t(editFulfillmentOrderSplit ? 'EDITSPLITFULFILLMENTORDER' : 'SPLITFULFILLMENTORDER')}
								className="icon icon--grid-action icon--split--white spc--right--tny"
								onClick={openSplitFulfillmentOrderModal}
							></i>
							{t(editFulfillmentOrderSplit ? 'EDITSPLITFULFILLMENTORDER' : 'SPLITFULFILLMENTORDER')}
						</button>
					</div>
				)}
			{/* Cancel */}
			{_.findIndex(onCancelStatuses, status => status == item.fulfillmentOrderStatus.abrv) !== -1 &&
				(isSuperAdminOrganization || nonCancelHoldReason()) && (
					<div className="popover__menu__item">
						<button data-cy={'cancelFulfillmentOrder'} className="btn btn--white" onClick={openCancelModal}>
							<i
								title={isSuperAdminOrganization ? t('CANCELFULFILLMENTORDER') : t('REQUESTTOBECANCELLED')}
								className="fa fa-window-close spc--right--tny k-custom-clickable"
							/>
							{isSuperAdminOrganization ? t('ORDERDETAILVIEW.CANCELFULFILLMENTORDER') : t('REQUESTTOBECANCELLED')}
						</button>
					</div>
				)}

			{/* Reoptimize */}
			{isSuperAdminOrganization &&
				_.findIndex(onReoptimizeStatuses, status => status == item.fulfillmentOrderStatus.abrv) != -1 && (
					<div className="popover__menu__item">
						<button
							data-cy={'reoptimize'}
							className="btn btn--white"
							onClick={() => openConfirmationModal(getReoptimizeConfirmModalProps())}
						>
							<i
								title={t('REOPTIMIZE')}
								className="icon icon--grid-action icon--order-repeat--white spc--right--tny"
							></i>
							{t('REOPTIMIZE')}
						</button>
					</div>
				)}

			{/* Never Shipped */}
			{(isSeller || isSuperAdminOrganization) &&
				_.findIndex(onNeverShippedStatuses, status => status == item.fulfillmentOrderStatus.abrv) != -1 && (
					<div className="popover__menu__item">
						<button data-cy={'neverShipped'} className="btn btn--white" onClick={openNeverShippedModal}>
							<i
								title={t('NEVERSHIPPED')}
								className="icon icon--grid-action icon--never-shipped--white spc--right--tny k-custom-clickable"
							></i>
							{t('NEVERSHIPPED')}
						</button>
					</div>
				)}
			{/* Mark as Shipped */}
			{(isSeller || isSuperAdminOrganization || isLtlOrder) &&
				_.findIndex(onMarkAsShippedStatuses, status => status == item.fulfillmentOrderStatus.abrv) != -1 && (
					<div className="popover__menu__item">
						<button data-cy={'markAsShipped'} className="btn btn--white" onClick={openMarkAsShippedModal}>
							<i title={t('MARKASSHIPPED')} className="fa fa-truck spc--right--tny k-custom-clickable"></i>
							{t('MARKASSHIPPED')}
						</button>
					</div>
				)}
			{/* Edit Order */}
			{(isSeller || isSuperAdminOrganization) &&
				!hasLabel &&
				item.fulfillmentOrderStatus.abrv != fulfillmentOrderStatus.canceled &&
				rootStore.authStore.hasPiiAccess && (
					<div className="popover__menu__item">
						<button
							data-cy={'editOrderInformation'}
							className="btn btn--white"
							type="button"
							onClick={openEditOrderModal}
						>
							<i title={t('EDIT')} className="fa fa-edit spc--right--tny k-custom-clickable"></i>
							{t('FULFILLMENTORDER.EDITMODAL.TITLE')}
						</button>
					</div>
				)}
			{/* Uncancel Fulfillment Order */}
			{isSuperAdminOrganization &&
				_.findIndex(onUncancelStatuses, status => status == item.fulfillmentOrderStatus.abrv) != -1 && (
					<div className="popover__menu__item">
						<button
							data-cy={'uncancelFulfillmentOrder'}
							className="btn btn--white"
							onClick={() => openConfirmationModal(getUncancelConfirmModalProps())}
						>
							<i title={t('UNCANCELFULFILLMENTORDER')} className="fa fa-recycle spc--right--tny k-custom-clickable"></i>
							{t('ORDERDETAILVIEW.UNCANCELFULFILLMENTORDER')}
						</button>
					</div>
				)}
			{/* Void Label */}
			{(isSeller || isSuperAdminOrganization) &&
			(isVendorOrder ? process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'staging' : true) && // Show VoidLabel for test environments for vendor
				hasLabel &&
				!isLtlOrder &&
				_.findIndex(voidLabelStatuses, status => status == item.fulfillmentOrderStatus.abrv) != -1 && (
					<div className="popover__menu__item">
						<button data-cy={'voidLabel'} className="btn btn--white" onClick={() => openVoidLabelConfirmModal(true)}>
							<i title={t('VOIDLABEL')} className="fa fa-undo spc--right--tny k-custom-clickable"></i>
							{t('VOIDLABELVIEW.VOIDLABEL')}
						</button>
					</div>
				)}
			{/* Void BOL */}
			{(isLtlOrder || isLtlShipment) && _.get(fulfillmentOrder, 'billOfLading.source') != null && (
				<div className="popover__menu__item">
					<button
						data-cy={'voidLabel'}
						className="btn btn--white"
						onClick={() => openConfirmationModal(getVoidBOLConfirmModalProps())}
					>
						<i title={t('VOIDBOL')} className="fa fa-undo spc--right--tny k-custom-clickable"></i>
						{t('VOIDBOLVIEW.VOIDBOL')}
					</button>
				</div>
			)}
			{/* Production Mode. */}
			{(isSeller || isSuperAdminOrganization) &&
				(fulfillmentOrder.order.channel.channelType.abrv == 'amazon' ||
					fulfillmentOrder.order.channel.channelType.abrv == 'goflow' ||
					true) &&
				rootStore.authStore.hasPiiAccess &&
				_.findIndex(onProductionModeStatuses, status => status == item.fulfillmentOrderStatus.abrv) != -1 &&
				process.env.NODE_ENV !== 'production' && (
					<div data-cy={'productionMode'} className="popover__menu__item--alt">
						<i title={t('ORDERVIEW.PRODUCTIONMODE')} className="icon icon--med icon--document-lock spc--right--tny"></i>
						<span>{t('ORDERVIEW.PRODUCTIONMODE')}</span>
						<div className="display--ib align--v--middle spc--left--tny">
							<BaasicCheckbox
								className={'input--toggle input--toggle--production'}
								id={'production-mode-toggle'}
								checked={fulfillmentOrder.productionMode}
								onChangeCallback={event =>
									openConfirmationModal(getProductionModeConfirmModalProps(event.currentTarget.checked))
								}
							/>
						</div>
					</div>
				)}
		</React.Fragment>
	);
});

const renderConfirmShipmentIcon = (item, confirmShipmentInProgress, confirmShipmentFailed) => {
	if (confirmShipmentInProgress) {
		return <div className="spinner-inline-small shipment-status__icon"></div>;
	}

	if (confirmShipmentFailed) {
		return <i className="type--color--error fa fa-times-circle shipment-status__icon" title="Retry Confirm Shipment" />;
	}

	const statusName = item.confirmShipmentStatus && item.confirmShipmentStatus.name;

	if (statusName === confirmShipmentStatus.confirmed) {
		return <i className="type--color--success fa fa-check-circle shipment-status__icon" />;
	}

	if ([confirmShipmentStatus.pending, confirmShipmentStatus.submitted].includes(statusName)) {
		return <i className="type--color--warning-light fa fa-exclamation-circle shipment-status__icon" />;
	}

	return null;
};

// PRO/BOL table for LTL shipments. Only a Priority1 booking captures both numbers: it keeps the BOL on the
// shipment (`bolNumber`, mirrored on the order) and leaves `trackingNumber` for the PRO, which the carrier
// publishes after dispatch - so a missing PRO renders as an intentional empty cell with a muted placeholder
// + tooltip. Every other LTL flow (Banyan/SellerCloud pull, mark as shipped, BOL upload) keeps the BOL in
// `trackingNumber` and never gets a PRO at all, so the PRO column is dropped unless some row is a Priority1
// booking. Voided shipments stay listed as struck-through rows. `proNumber` (from the Get PRO Number
// action) only applies to the active row.
const LtlLabelsTable = observer(({ shipmentInfos, proNumber, priority1BolNumber, t }) => {
	const rows = _.orderBy(shipmentInfos, info => (info.status === shipmentInfoStatusEnum.active ? 0 : 1));
	// Row-level first: voiding a booking clears the order-level number but the voided row keeps its own.
	const resolveBolNumber = info =>
		info.bolNumber || (info.status === shipmentInfoStatusEnum.active ? priority1BolNumber : null);
	// Bookings predating the move of the BOL off trackingNumber still hold it there, so a row whose
	// trackingNumber is only its BOL over again has no PRO of its own.
	const hasProNumber = info => {
		const bolNumber = resolveBolNumber(info);
		return !!bolNumber && info.trackingNumber !== bolNumber;
	};
	const hasProColumn = _.some(rows, hasProNumber);
	return (
		<table className="table--secondary table--secondary--labels spc--top--sml">
			<thead>
				<tr>
					{hasProColumn && <th>{t('ORDERDETAILVIEW.PRONUMBERCOLUMN')}</th>}
					<th>{t('ORDERDETAILVIEW.BOLNUMBERCOLUMN')}</th>
				</tr>
			</thead>
			<tbody>
				{_.map(rows, (info, idx) => {
					const isActive = info.status === shipmentInfoStatusEnum.active;
					const isVoided = info.status === shipmentInfoStatusEnum.voided;
					const bolNumber = resolveBolNumber(info);
					const isProRow = hasProNumber(info);
					const proValue = isProRow ? (isActive && proNumber) || info.trackingNumber : null;
					const voidedStyle = isVoided ? { textDecoration: 'line-through', color: 'grey' } : undefined;
					return (
						<tr key={idx}>
							{hasProColumn && (
								<td data-cy={isVoided ? 'voidedTrackingNumbers' : 'proNumber'} style={voidedStyle}>
									{proValue ? (
										proValue
									) : isActive && isProRow ? (
										<React.Fragment>
											<span className="type--color--grey-5">{t('ORDERDETAILVIEW.PRONUMBERNOTAVAILABLE')}</span>
											<i
												className="fas fa-info-circle spc--left--nano"
												data-tip={t('ORDERDETAILVIEW.PRONUMBERNOTAVAILABLETOOLTIP')}
												data-cy={'proNumberNotAvailableTooltip'}
											/>
										</React.Fragment>
									) : (
										'-'
									)}
								</td>
							)}
							<td data-cy={isVoided && !hasProColumn ? 'voidedTrackingNumbers' : 'bolNumber'} style={voidedStyle}>
								{bolNumber || info.trackingNumber || '-'}
							</td>
						</tr>
					);
				})}
			</tbody>
		</table>
	);
});

export default defaultTemplate(OrderDetailTemplate);
